/**
 * OpenAI-compatible provider factory.
 *
 * Mistral and Groq both expose an OpenAI-compatible /chat/completions API, so a
 * single configurable provider serves both. Drop in MISTRAL_API_KEY or
 * GROQ_API_KEY and the corresponding provider activates automatically on the
 * next start, with no further code change. This is how Nuvra keeps the "add a
 * key and it just works" contract across every OpenAI-compatible backend.
 *
 * Behaviour mirrors openaiProvider.ts: fail-closed through a per-service circuit
 * breaker, no fabricated success responses, returns empty files so aiRouter can
 * fall back to the next provider.
 */

import OpenAI from "openai";
import type {
  AIProvider,
  AIRequest,
  AIResponse,
  AIStreamChunk,
  AIError,
} from "./aiProvider";
import { HttpCircuitBreaker, CircuitOpenError } from "../../runtime/httpCircuitBreaker";

export interface OpenAICompatibleConfig {
  /** Provider name, e.g. "mistral" or "groq". */
  name: string;
  /** Env var that holds the key. Presence of this key activates the provider. */
  apiKeyEnv: string | string[];
  /** OpenAI-compatible base URL, e.g. https://api.mistral.ai/v1. */
  baseURL: string;
  /** Default model when the request and router do not specify one. */
  defaultModel: string;
  /** Circuit breaker guarding this provider's calls. */
  circuit: HttpCircuitBreaker;
  /** Whether the backend supports response_format: json_object. Default true. */
  supportsJSON?: boolean;
  /** Whether the backend supports tool/function calling. Default true. */
  supportsTools?: boolean;
}

const SYSTEM_FALLBACK =
  "You are a strict code generator. Return ONLY valid JSON: { files: [{ name, content }] }";

function resolveKey(apiKeyEnv: string | string[]): string | undefined {
  const names = Array.isArray(apiKeyEnv) ? apiKeyEnv : [apiKeyEnv];
  for (const n of names) {
    const v = process.env[n];
    if (v) return v;
  }
  return undefined;
}

export function createOpenAICompatibleProvider(cfg: OpenAICompatibleConfig): AIProvider {
  const supportsJSON = cfg.supportsJSON !== false;
  const supportsTools = cfg.supportsTools !== false;

  let client: OpenAI | null = null;
  function getClient(): OpenAI {
    if (!client) {
      client = new OpenAI({ apiKey: resolveKey(cfg.apiKeyEnv)!, baseURL: cfg.baseURL });
    }
    return client;
  }

  return {
    name: cfg.name,

    capabilities: {
      streaming: true,
      tools: supportsTools,
      jsonMode: supportsJSON,
    },

    isAvailable() {
      return !!resolveKey(cfg.apiKeyEnv);
    },

    async generate(req: AIRequest): Promise<AIResponse> {
      const start = Date.now();

      if (!resolveKey(cfg.apiKeyEnv)) {
        return {
          files: [],
          error: {
            message: `Missing ${Array.isArray(cfg.apiKeyEnv) ? cfg.apiKeyEnv[0] : cfg.apiKeyEnv} - provider not configured`,
            provider: cfg.name,
            retryable: false,
          },
        };
      }

      try {
        return await cfg.circuit.execute(async () => {
          const completion = await getClient().chat.completions.create({
            model: req.model || cfg.defaultModel,
            temperature: req.temperature ?? 0.7,
            max_tokens: req.maxTokens,
            ...(supportsJSON ? { response_format: { type: "json_object" as const } } : {}),
            messages: [
              { role: "system", content: req.systemPrompt || SYSTEM_FALLBACK },
              { role: "user", content: req.prompt },
            ],
            ...(req.signal ? { signal: req.signal } : {}),
          });

          const text = completion.choices[0]?.message?.content || "{}";

          let files: { name: string; content: string }[] = [];
          try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed)) files = parsed;
            else if (Array.isArray(parsed?.files)) files = parsed.files;
          } catch {
            // non-JSON: return empty so aiRouter retries the next provider
          }

          return {
            files,
            raw: text,
            usage: completion.usage
              ? {
                  promptTokens: completion.usage.prompt_tokens,
                  completionTokens: completion.usage.completion_tokens,
                  totalTokens: completion.usage.total_tokens,
                  provider: cfg.name,
                }
              : undefined,
            meta: {
              provider: cfg.name,
              model: completion.model || req.model || cfg.defaultModel,
              latencyMs: Date.now() - start,
            },
          };
        });
      } catch (err: any) {
        const error: AIError = {
          message: err?.message || `${cfg.name} generation failed`,
          provider: cfg.name,
          retryable: !(err instanceof CircuitOpenError),
        };
        return { files: [], error };
      }
    },

    async stream(req: AIRequest, onChunk: (chunk: AIStreamChunk) => void): Promise<void> {
      let accumulated = "";
      try {
        if (!resolveKey(cfg.apiKeyEnv)) throw new Error(`Missing ${cfg.name} API key`);

        const stream = await getClient().chat.completions.create({
          model: req.model || cfg.defaultModel,
          temperature: req.temperature ?? 0.7,
          max_tokens: req.maxTokens,
          stream: true,
          messages: [
            { role: "system", content: req.systemPrompt || SYSTEM_FALLBACK },
            { role: "user", content: req.prompt },
          ],
        });

        for await (const part of stream) {
          const delta = part.choices?.[0]?.delta?.content;
          if (delta) {
            accumulated += delta;
            onChunk({ content: delta });
          }
        }

        onChunk({ content: accumulated, done: true });
      } catch (err: any) {
        const error: AIError = {
          message: err?.message || `${cfg.name} streaming failed`,
          provider: cfg.name,
          retryable: true,
        };
        onChunk({ error, done: true });
      }
    },
  };
}
